import React, { useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import {
  BuildingStorefrontIcon,
  CalendarIcon,
  TagIcon,
} from '@heroicons/react/16/solid';
import { Dialog } from '../../common/Catalyst/dialog';
import { Button } from '../../common/Catalyst/button';
import {
  HoaReturn,
  VendorReturn,
  WorkOrderReturn,
} from '../../clients/rust-api-client';
import { vendorService, workOrderService } from '../../clients/ApiClients';
import { apiErrorMessage } from '../../utils/apiErrors';
import { ErrorAlert } from '../../common/Custom/ErrorAlert';
import {
  DatePicker,
  PropertyPicker,
} from '../../violation/linear/PropertyPicker';
import { SeverityGlyph } from '../../violation/linear/StatusIcon';
import { formatDay } from '../../violation/linear/violationMeta';
import { CreateScope } from '../../shell/createScope';
import { ScopeChips, useForHoa, useScopeState } from '../../shell/ScopeChips';
import { useSyncedVendors } from '../../sync/hooks';
import { WorkOrderStatusIcon } from './WorkOrderStatusIcon';
import {
  DESCRIPTION_MAX,
  PRIORITIES,
  STATUSES,
  TITLE_MAX,
  WORK_ORDER_TYPES,
  WorkOrderStatus,
  buildWorkOrderCreate,
  isValidEstimate,
  missingForWorkOrder,
  priorityAsSeverity,
  statusLabel,
  titleCase,
} from './workOrderMeta';

/**
 * Create a work order the Linear way (#632, after #603 Part 2 §6): a large
 * title, a free-form description, and a row of property chips — the same
 * pickers the work order's properties panel edits with, so create and
 * edit look alike. Replaces the label-over-input dialog that lived inline
 * in WorkOrderList.
 *
 * The estimate, assignee and notes are free text, not a choice from a
 * list, so they stay plain labelled inputs below the chips.
 */
interface Props {
  open: boolean;
  hoas: HoaReturn[];
  hoasLoading?: boolean;
  scope?: CreateScope;
  /** The page fixed the scope it passed; those chips are not changeable. */
  lockScope?: boolean;
  onClose: () => void;
  onCreated: (workOrder: WorkOrderReturn) => void;
}

const NO_VENDOR = '__none__';

const activeByName = (vendors: VendorReturn[]): VendorReturn[] =>
  vendors
    .filter((v) => v.status !== 'inactive')
    .sort((a, b) => a.name.localeCompare(b.name));

const fetchVendors = async (hoaId: string): Promise<VendorReturn[]> =>
  activeByName((await vendorService.getVendorsByHoa({ hoaId })).data.vendors);

export const CreateWorkOrderModal: React.FC<Props> = ({
  open,
  hoas,
  hoasLoading,
  scope = {},
  lockScope,
  onClose,
  onCreated,
}) => {
  const { hoaId, setHoaId, homeId, setHomeId } = useScopeState(
    open,
    scope,
    hoas
  );
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState<string | null>(null);
  const [priority, setPriority] = useState<string>('Medium');
  const [status, setStatus] = useState<WorkOrderStatus>('Pending');
  const [scheduled, setScheduled] = useState('');
  const [vendorId, setVendorId] = useState<string | null>(null);
  const [assignedTo, setAssignedTo] = useState('');
  const [estimate, setEstimate] = useState('');
  const [notes, setNotes] = useState('');
  const [showNotes, setShowNotes] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The sync engine's copy when it has one (#561); fetched otherwise.
  const syncedVendors = useSyncedVendors({ hoaId });
  const vendors = useForHoa(
    hoaId,
    fetchVendors,
    'vendors',
    useMemo(
      () => (hoaId && syncedVendors ? activeByName(syncedVendors) : null),
      [hoaId, syncedVendors]
    )
  );

  useEffect(() => {
    if (!open) return;
    setTitle('');
    setDescription('');
    setType(null);
    setPriority('Medium');
    setStatus('Pending');
    setScheduled('');
    setAssignedTo('');
    setEstimate('');
    setNotes('');
    setShowNotes(false);
    setVendorId(scope.vendorId || null);
    setError(null);
    // Only on open: the scope object is rebuilt on every render upstream.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // A vendor belongs to one association: changing it drops the vendor.
  const changeHoa = (id: string) => {
    setHoaId(id);
    setVendorId(null);
  };

  const vendor = vendors.items.find((v) => v.id === vendorId);
  const draft = {
    hoaId,
    homeId,
    title,
    description,
    type,
    priority,
    status,
    scheduled,
    vendorId,
    vendorName: vendor?.name ?? null,
    assignedTo,
    estimatedCost: estimate,
    notes,
  };
  const missing = missingForWorkOrder(draft);
  const estimateValid = isValidEstimate(estimate);
  const request = buildWorkOrderCreate(draft);
  const canSubmit = !!request && !saving;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!request || saving) return;
    setSaving(true);
    setError(null);
    try {
      const response = await workOrderService.createWorkOrder({
        workOrderCreate: request,
      });
      onCreated(response.data);
      onClose();
    } catch (err) {
      setError(
        apiErrorMessage(
          err,
          'The work order was not created. Please try again.'
        )
      );
    } finally {
      setSaving(false);
    }
  };

  const placeholderChip = (label: string, icon?: React.ReactNode) => (
    <span className="inline-flex items-center gap-1.5 text-fg-muted">
      {icon}
      {label}
    </span>
  );
  const inputClass =
    'rounded-md border border-line bg-transparent px-2 py-1 text-[13px] text-fg placeholder:text-fg-faint focus:outline-none focus:ring-2 focus:ring-accent';

  return (
    <Dialog open={open} onClose={() => !saving && onClose()} size="2xl">
      <form onSubmit={submit} aria-label="New work order">
        <p className="text-[12px] font-medium text-fg-muted">New work order</p>

        <input
          autoFocus
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          maxLength={TITLE_MAX}
          placeholder="What needs doing?"
          aria-label="Title"
          className="mt-2 w-full border-0 bg-transparent p-0 text-xl font-semibold text-fg placeholder:text-fg-faint focus:outline-none focus:ring-0"
        />
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          maxLength={DESCRIPTION_MAX}
          rows={4}
          placeholder="Describe the work — where, what is wrong, what done looks like."
          aria-label="Description"
          className="mt-3 w-full resize-none border-0 bg-transparent p-0 text-[15px]/6 text-fg-secondary placeholder:text-fg-faint focus:outline-none focus:ring-0"
        />

        <div className="mt-2 flex flex-wrap items-center gap-1 border-t border-line/60 pt-3">
          <ScopeChips
            hoas={hoas}
            hoasLoading={hoasLoading}
            hoaId={hoaId}
            onHoaChange={changeHoa}
            lockHoa={lockScope && !!scope.hoaId}
            memberId={scope.memberId}
            home={{
              homeId,
              onChange: setHomeId,
              locked: lockScope && !!scope.homeId,
              optional: true,
            }}
          />
          <PropertyPicker
            options={STATUSES.map((s) => ({
              value: s.value,
              label: s.label,
              icon: <WorkOrderStatusIcon status={s.value} />,
            }))}
            value={status}
            onChange={(v) => setStatus(v as WorkOrderStatus)}
            ariaLabel={`Status: ${statusLabel(status)}`}
            trigger={
              <>
                <WorkOrderStatusIcon status={status} />
                {statusLabel(status)}
              </>
            }
          />
          <PropertyPicker
            options={PRIORITIES.map((p) => ({
              value: p,
              label: p,
              icon: <SeverityGlyph severity={priorityAsSeverity(p)} />,
            }))}
            value={priority}
            onChange={setPriority}
            ariaLabel={`Priority: ${priority}`}
            trigger={
              <>
                <SeverityGlyph severity={priorityAsSeverity(priority)} />
                {priority}
              </>
            }
          />
          <PropertyPicker
            options={WORK_ORDER_TYPES.map((t) => ({
              value: t,
              label: titleCase(t),
            }))}
            value={type}
            onChange={setType}
            ariaLabel={type ? `Type: ${titleCase(type)}` : 'Choose type'}
            trigger={
              type ? (
                <>
                  <TagIcon className="h-3.5 w-3.5 text-fg-muted" aria-hidden />
                  {titleCase(type)}
                </>
              ) : (
                placeholderChip(
                  'Type',
                  <TagIcon className="h-3.5 w-3.5" aria-hidden />
                )
              )
            }
          />
          <DatePicker
            value={scheduled}
            onChange={setScheduled}
            ariaLabel={
              scheduled
                ? `Scheduled: ${formatDay(`${scheduled}T12:00:00Z`)}`
                : 'Set scheduled date'
            }
            trigger={
              scheduled ? (
                <>
                  <CalendarIcon
                    className="h-3.5 w-3.5 text-fg-muted"
                    aria-hidden
                  />
                  Scheduled {formatDay(`${scheduled}T12:00:00Z`)}
                </>
              ) : (
                placeholderChip(
                  'Schedule',
                  <CalendarIcon className="h-3.5 w-3.5" aria-hidden />
                )
              )
            }
          />
          <PropertyPicker
            options={[
              { value: NO_VENDOR, label: 'No vendor' },
              ...vendors.items.map((v) => ({ value: v.id, label: v.name })),
            ]}
            value={vendorId ?? NO_VENDOR}
            onChange={(v) => setVendorId(v === NO_VENDOR ? null : v)}
            placeholder="Vendor…"
            ariaLabel={vendor ? `Vendor: ${vendor.name}` : 'Choose vendor'}
            readOnly={
              !hoaId || vendors.loading || (lockScope && !!scope.vendorId)
            }
            trigger={
              vendor ? (
                <>
                  <BuildingStorefrontIcon
                    className="h-3.5 w-3.5 text-fg-muted"
                    aria-hidden
                  />
                  {vendor.name}
                </>
              ) : (
                placeholderChip(
                  vendors.loading ? 'Loading vendors…' : 'Vendor',
                  <BuildingStorefrontIcon className="h-3.5 w-3.5" aria-hidden />
                )
              )
            }
          />
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2">
          <label className="flex items-center gap-2 text-[13px] text-fg-secondary">
            Assigned to
            <input
              value={assignedTo}
              onChange={(e) => setAssignedTo(e.target.value)}
              placeholder="Name"
              className={clsx(inputClass, 'w-36')}
            />
          </label>
          <label className="flex items-center gap-2 text-[13px] text-fg-secondary">
            Estimate
            <span
              className={clsx(
                'flex items-center rounded-md border px-2 py-1',
                estimateValid ? 'border-line' : 'border-danger'
              )}
            >
              <span className="text-fg-muted">$</span>
              <input
                inputMode="decimal"
                value={estimate}
                onChange={(e) =>
                  setEstimate(e.target.value.replace(/[^\d.]/g, ''))
                }
                placeholder="0.00"
                aria-invalid={!estimateValid}
                className="w-24 border-0 bg-transparent p-0 pl-1 text-[13px] text-fg focus:outline-none focus:ring-0"
              />
            </span>
          </label>
          {!showNotes && (
            <button
              type="button"
              onClick={() => setShowNotes(true)}
              className="text-[13px] text-accent-fg hover:underline"
            >
              Add notes
            </button>
          )}
        </div>
        {showNotes && (
          <textarea
            autoFocus
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            placeholder="Internal notes — access codes, who to call."
            aria-label="Notes"
            className={clsx(inputClass, 'mt-3 w-full resize-none')}
          />
        )}

        <ErrorAlert className="mt-3">{error}</ErrorAlert>

        <div className="mt-5 flex flex-wrap items-center justify-end gap-2 border-t border-line/60 pt-4">
          {missing.length > 0 && (title || description || type) && (
            <p className="mr-auto text-[12px] text-fg-muted">
              Still needs {missing.join(', ')}.
            </p>
          )}
          <Button plain type="button" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            color="sky"
            type="submit"
            disabled={!canSubmit}
            title={missing.length ? `Add ${missing.join(', ')}` : undefined}
          >
            {saving ? 'Creating…' : 'Create work order'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
};
